import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isOpenerVariant,
  showsRarityBackdrop,
  showsRipButton,
  type AnimationConfig,
  type CardBadge,
  type CardFact,
  type OpenerVariant,
} from '@game-platform/pack-animation';
import { Button } from '../../design-system/Button';
import type { IframePoolOrder } from '../../api';
import { haptic } from '../../haptics';
import { glowColorFor } from '../glowColorFor';
import { sleekRarityColor } from '../sleek/rarityColor';
import { RarityGlow } from '../sleek/RarityGlow';
import { pedestalSrcFor } from '../pedestalSrc';
import { NO_READY_ACTIONS, useReportedActions, type ReadyActionsListener } from '../readyActions';
import { useMuted } from '../soundSettings';
import type { StageReserves } from '../stageLayout';
import { PackOpenerStage, type PackOpenerStageHandle } from './PackOpenerStage';
import { useWinnerBands } from './useWinnerBands';
import { useWinnerCardRest } from './useWinnerCardRest';
import { SleekButton, useIsSleek } from '../../theme';

export interface OpenerReadyStepProps {
  offer: IframePoolOrder['poolOffer'];
  animation: AnimationConfig;
  /** The real card once `mark_as_opened` answered — its artwork rides into the scene. */
  cardImageUrl: string | null;
  /** Rarity of the pull once known — tints the scene's halo, names the carousel's banner. */
  badge: string | null;
  /** The pull's tier once known, low tiers included — sleek colours every
   *  tier's halo (`badge` stays empty for Common / Uncommon). */
  tier?: string | null;
  /** What the carousel tells about the pull once known; empty until then. */
  facts?: CardFact[];
  /** The pack is open (slice / burst) or chosen (carousel): resolve the card
   *  on the backend. Resolves to whether the card came. */
  onOpened: () => Promise<boolean>;
  /** The ceremony is over. Returns false when the open never resolved. */
  onRevealed: () => boolean;
  /** The scene cannot run here — the caller falls back to the slot lane. */
  onError: (message: string) => void;
  /** Laid out in a box of its own rather than the screen: the bands the
   *  host's chrome takes there, as lengths of that box (a size container).
   *  The card then rests in the box, measured off it. */
  contained?: StageReserves;
  /** The player's mute, for a host with a switch of its own; the reveal's otherwise. */
  muted?: boolean;
  /** A host that draws the RIP! / Shuffle pill itself hears it here (see `useReportedActions`); the step then
   *  renders no pill of its own. The hidden "Open pack" of a gesture-only mechanic stays the step's. */
  onActionsChange?: ReadyActionsListener;
}

// `loading` until the scene has drawn the pack: RIP! pressed before that
// would do nothing. `chosen` is the carousel's: a copy picked and lifted,
// the open request in flight or answered, the second tap still to come
type Phase = 'loading' | 'idle' | 'chosen' | 'opening' | 'done';

/** Nothing to tell yet — one array, so the scene is not retuned every render. */
const NO_FACTS: CardFact[] = [];

/**
 * Ready step for the library mechanics (slice / burst / carousel): the
 * pack-opener-js scene where the slot lane would be, plus the same RIP!
 * button — for players who would rather not swipe, and for anyone with
 * reduced motion on. On the carousel's ring the button shuffles instead:
 * the pack is chosen with a tap, and the RIP! comes back once it is
 * chosen, for the second tap.
 *
 * The scene covers the whole step and the ceremony plays in the winner's
 * card region: the top (title) and bottom (panel) bands the winner chrome
 * reserves are kept back from the scene too, measured from the very CSS
 * lengths the chrome uses so the two can never drift. The card and its stand
 * settle centred where the winner's own card would sit, the title and the
 * panel then arrive around them; the pack is laid out in that band as well,
 * so the cut does not hoist the card up out of it. The scene's glow still
 * runs under the bands — the canvas is not cut off at their edges.
 *
 * Slice and burst open the pack on the backend when the lid is off — the
 * reveal that follows buys the request its time. The carousel opens it on
 * the first tap: the chosen pack floats, deaf to the finger, until the card
 * is known, and the story it then tells is the card's own. A request that
 * fails puts the pack back on the ring.
 */
export function OpenerReadyStep({
  offer,
  animation,
  cardImageUrl,
  badge,
  tier = null,
  facts,
  onOpened,
  onRevealed,
  onError,
  contained,
  muted: hostMuted,
  onActionsChange,
}: OpenerReadyStepProps) {
  const stageRef = useRef<PackOpenerStageHandle>(null);
  const isSleek = useIsSleek();
  const [phase, setPhase] = useState<Phase>('loading');
  // The card is in: a chosen pack may be opened by the pill as well as the finger
  const [resolved, setResolved] = useState(false);

  // Latest callbacks in refs: PackReveal re-renders on every machine change
  // and must not remount or retarget the scene mid-ceremony
  const handlers = useRef({ onOpened, onRevealed, onError });
  handlers.current = { onOpened, onRevealed, onError };

  const packUrl = offer.packImageUrl;
  useEffect(() => {
    if (!packUrl) handlers.current.onError('pack has no artwork');
  }, [packUrl]);

  const { bands, probes } = useWinnerBands(contained);
  // The operator may hide the Rip button for a mechanic that opens by gesture
  // (design_system.animation.controls). Nothing then sits under the pack, so
  // the scene keeps the same band top and bottom and the pack is centred on
  // the screen. Constant for the scene's life — a change mid-ceremony waits
  // for a reset anyway.
  const hasRipButton = showsRipButton(animation);
  const reserveBottom = hasRipButton ? bands.bottom : bands.top;
  // Where the winner's own card would sit: the scene rests its card there,
  // and the winner then arrives around it — nothing is drawn twice
  const { rest, probe: restProbe } = useWinnerCardRest(contained);

  // The ceremony's score is the library's, on the operator's sound settings;
  // the player's mute is the one thing the host adds to it
  const revealMuted = useMuted();
  const muted = hostMuted ?? revealMuted;
  const variant: OpenerVariant = isOpenerVariant(animation.variant) ? animation.variant : 'slice';
  const opensOnCommit = variant === 'carousel';
  // Sleek's card glows in its own rarity palette, the same hue as the glow
  // behind the card on its Vault card page.
  const sceneGlow = (isSleek ? sleekRarityColor(tier) : null) ?? glowColorFor(badge);
  // The slot lane's giant rarity word, when the operator wants it behind this
  // mechanic's card too — Rare and above, which is what `badge` holds. Not in
  // sleek, whose page stays dark after the pull like the slot lane's there.
  // The scene writes it, under its card, as the card lands
  const hasRarityBackdrop = !isSleek && badge !== null && showsRarityBackdrop(animation);

  // The banner the carousel runs, and the word behind slice and burst's card,
  // in the tier's colour — for the tiers that get one, which is what `badge`
  // holds (the low tiers come as null)
  const sceneBadge = useMemo<CardBadge | undefined>(
    () => (badge ? { label: badge, color: glowColorFor(badge) } : undefined),
    [badge],
  );

  // Stable for the scene's lifetime — see PackOpenerStage on why.
  // The scene reports ready again after every re-layout; only the first arms
  // the pill, a later one must not unlock a pack that is opening
  const handleReady = useCallback(
    () => setPhase((current) => (current === 'loading' ? 'idle' : current)),
    [],
  );

  const handleCommitted = useCallback(() => {
    if (!opensOnCommit) return;
    // Same buzz as picking a pack off the slot lane
    haptic('land');
    setPhase('chosen');
    // The pack floats deaf until the card is known: the story it tells on
    // the second tap has to be this card's. A request that fails (toast
    // already raised) puts the pack back on the ring
    stageRef.current?.setEnabled(false);
    void handlers.current.onOpened().then((came) => {
      if (came) {
        setResolved(true);
        stageRef.current?.setEnabled(true);
        return;
      }
      stageRef.current?.reset();
      setPhase('idle');
    });
  }, [opensOnCommit]);

  const handleOpened = useCallback(() => {
    // The rip itself — the lid off under the finger or the pill
    haptic('land');
    setPhase('opening');
    // Slice and burst open the pack here; the carousel already has at the commit
    if (!opensOnCommit) void handlers.current.onOpened();
  }, [opensOnCommit]);

  const handleRevealed = useCallback(() => {
    if (handlers.current.onRevealed()) {
      // The card is in — the opener's counterpart of the reel landing
      haptic('land');
      setPhase('done');
      return;
    }
    // The open failed (toast already raised) — put the pack back together
    stageRef.current?.reset();
    setPhase('idle');
  }, []);

  const handleError = useCallback((message: string) => handlers.current.onError(message), []);

  // On the carousel's ring the pill shuffles; a chosen pack is opened by it
  // once the card is in — until then the pack floats and the pill waits too
  const shuffling = opensOnCommit && phase === 'idle';
  const pillDisabled = shuffling ? false : phase === 'chosen' ? !resolved : phase !== 'idle';
  const onPill = () => (shuffling ? stageRef.current?.shuffle() : stageRef.current?.play());
  const pillLabel = shuffling ? 'Shuffle' : isSleek ? 'Rip' : 'RIP!';
  const lifted = onActionsChange !== undefined;
  useReportedActions(
    onActionsChange,
    packUrl && hasRipButton && phase !== 'done'
      ? [{ id: shuffling ? 'shuffle' : 'rip', label: pillLabel, run: onPill, disabled: pillDisabled }]
      : NO_READY_ACTIONS,
  );

  if (!packUrl) return null;

  return (
    <div className="relative h-full w-full">
      {probes}
      {restProbe}

      {isSleek && rest && (
        <div
          aria-hidden
          className={`pointer-events-none absolute transition-opacity duration-500 ${phase === 'done' ? 'opacity-100' : 'opacity-0'}`}
          // Measured geometry — where the scene rests its card
          style={{ left: rest.card.left, top: rest.card.top, width: rest.card.width, height: rest.card.height }}
        >
          <RarityGlow badgeName={tier} />
        </div>
      )}

      <div className="absolute inset-0">
        <PackOpenerStage
          ref={stageRef}
          animation={animation}
          packUrl={packUrl}
          pedestalUrl={pedestalSrcFor(offer)}
          glow={sceneGlow}
          reserveTop={bands.top}
          reserveBottom={reserveBottom}
          {...(rest ? { rest } : {})}
          muted={muted}
          soundBadge={badge}
          onReady={handleReady}
          onCommitted={handleCommitted}
          onOpened={handleOpened}
          onRevealed={handleRevealed}
          onError={handleError}
          className="h-full w-full"
          {...(cardImageUrl ? { cardUrl: cardImageUrl } : {})}
          {...(opensOnCommit ? { facts: facts ?? NO_FACTS } : {})}
          {...((opensOnCommit || hasRarityBackdrop) && sceneBadge ? { badge: sceneBadge } : {})}
          badgeWord={hasRarityBackdrop}
        />
      </div>

      {/* Sleek's slice says how to open the pack, above it (partner Figma
          2026-10-14) — gone the moment the cut starts. The scene lays the
          pack out in the band under `bands.top`, so the line rides that edge. */}
      {isSleek && variant === 'slice' && (
        <p
          data-co-component="RevealSwipeHint"
          className={`pointer-events-none absolute inset-x-0 m-0 -translate-y-full pb-2 text-center text-[14px] leading-[20px] font-medium tracking-[-0.15px] text-[var(--co-RevealSwipeHint-color,#FFFFFF)] transition-opacity duration-300 ${phase === 'idle' ? 'opacity-100' : 'opacity-0'}`}
          // Measured band — where the scene's pack area starts
          style={{ top: bands.top, display: 'var(--co-RevealSwipeHint-display, block)' }}
        >
          Swipe to slice pack open
        </p>
      )}

      {/* Same pill as the slot lane's, so the two mechanics read as one product.
          It sits inside the bottom band the scene keeps clear. Once the ceremony
          is over it only hides (`invisible`): the winner's panel arrives in
          that band, over the scene. Hidden by the operator, the pack opens by
          gesture — the pill then stays only for keyboard and screen-reader
          players (and a gesture that fails): invisible until it takes focus,
          so a paid pack can always be opened. */}
      {!hasRipButton && phase !== 'done' && (
        <button
          type="button"
          onClick={onPill}
          disabled={pillDisabled}
          className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:inset-x-4 focus-visible:bottom-6 focus-visible:z-10 focus-visible:h-11 focus-visible:rounded-xl focus-visible:bg-white focus-visible:text-[#101114] focus-visible:font-semibold"
        >
          Open pack
        </button>
      )}
      {hasRipButton && !lifted && (
        <div
          className={`absolute inset-x-0 bottom-0 z-10 mx-auto w-full max-w-[357px] px-4 pb-6 ${
            phase === 'done' ? 'invisible' : ''
          }`}
        >
          {isSleek ? (
            <SleekButton name="RevealRipButton" onClick={onPill} disabled={pillDisabled} className="w-full">
              {pillLabel}
            </SleekButton>
          ) : (
            <Button variant="primary" onClick={onPill} disabled={pillDisabled} fullWidth className="h-16 type-headline">
              {pillLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
